import { useEffect, useState } from 'react';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';
import { StatCard } from '../components/widgets/StatCard';
import { Badge } from '../components/ui/Badge';
import { Analytics as AnalyticsApi } from '../api/client';
import { DollarSign, Eye, Heart, TrendingUp } from 'lucide-react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Button } from '../components/ui/Button';
import { RefreshCw } from 'lucide-react';
import { useStore } from '../store';

type Overview = {
  listings_total: number;
  listings_active: number;
  orders_total: number;
  revenue_total: number;
  views_30d: number;
  favorites_30d: number;
  conversion_30d_pct: number | null;
};

function fmtDelta(prior: number, current: number): number | null {
  if (prior <= 0) return null;
  return ((current - prior) / prior) * 100;
}

export function Analytics() {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [byListing, setByListing] = useState<any[]>([]);
  const [orderSeries, setOrderSeries] = useState<any[]>([]);
  const [syncing, setSyncing] = useState(false);
  const pushToast = useStore((s) => s.pushToast);

  const load = () => {
    Promise.all([
      AnalyticsApi.overview(),
      AnalyticsApi.listings().catch(() => []),
      AnalyticsApi.orders().catch(() => []),
    ]).then(([ov, ls, os]) => {
      setOverview(ov as Overview);
      setByListing(ls);
      setOrderSeries(os);
    });
  };
  useEffect(load, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await fetch('/api/analytics/sync', { method: 'POST' }).then((r) => r.json());
      if (r.ok) {
        pushToast({ kind: 'success', message: `Synced analytics for ${r.data?.listings ?? 0} listings` });
        load();
      } else {
        pushToast({ kind: 'error', message: r.error?.message ?? 'sync failed' });
      }
    } catch (e) {
      pushToast({ kind: 'error', message: (e as Error).message });
    } finally {
      setSyncing(false);
    }
  };

  // The /orders analytics endpoint orders DESC; chart wants oldest-first.
  const chartData = orderSeries.length > 0 ? [...orderSeries].reverse() : [];

  return (
    <div>
      <PageHeader
        kicker="// analytics · 07"
        title="Performance"
        description="Views, favorites, sales, revenue — at shop and listing level."
        actions={
          <Button variant="secondary" leftIcon={<RefreshCw size={13} className={syncing ? 'animate-spin' : ''} />} onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync analytics'}
          </Button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <StatCard kicker="revenue · 30d" value={overview ? `$${Number(overview.revenue_total).toFixed(2)}` : '—'} hint="lifetime" icon={DollarSign} accent="success" />
        <StatCard kicker="views · 30d" value={overview ? overview.views_30d.toLocaleString() : '—'} hint="visits" icon={Eye} accent="brand" />
        <StatCard kicker="favorites · 30d" value={overview ? overview.favorites_30d.toLocaleString() : '—'} hint="hearts" icon={Heart} accent="warn" />
        <StatCard
          kicker="conversion · 30d"
          value={overview?.conversion_30d_pct != null ? `${overview.conversion_30d_pct.toFixed(2)}%` : '—'}
          hint="sales / views"
          icon={TrendingUp}
          accent="neutral"
        />
      </div>

      <Card className="mb-4">
        <CardHeader
          kicker="// trend"
          title="Revenue · 14 days"
          description="Daily revenue across all listings."
          actions={<Badge tone="brand">live</Badge>}
        />
        <div className="h-64 -ml-2">
          {chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-[12px] text-ink-500">
              No order data yet. Click Sync from Etsy on the Orders page.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F1641E" stopOpacity={0.30} />
                    <stop offset="100%" stopColor="#F1641E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#EADFCB" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="date" stroke="#9C8A6E" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#9C8A6E" fontSize={11} tickLine={false} axisLine={false} width={40} />
                <Tooltip
                  contentStyle={{ background: '#FFFFFF', border: '1px solid #EADFCB', borderRadius: 12, fontSize: 12, boxShadow: '0 8px 24px -8px rgba(80,50,20,0.18)' }}
                  labelStyle={{ color: '#6F5E45' }}
                  cursor={{ stroke: 'rgba(241,100,30,0.35)', strokeWidth: 1 }}
                />
                <Area type="monotone" dataKey="revenue" stroke="#F1641E" strokeWidth={2.5} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="px-5 pt-5">
          <CardHeader kicker="// per-listing" title="Top listings" description="Views, favorites, sales per listing — last 30 days." />
        </div>
        <div className="grid grid-cols-[1fr_120px_120px_120px_120px] px-4 py-3 border-y border-cream-300 kicker bg-cream-50/40">
          <div>Listing</div>
          <div>Views</div>
          <div>Favorites</div>
          <div>Sales</div>
          <div className="text-right">Revenue</div>
        </div>
        {byListing.length === 0 ? (
          <div className="px-4 py-10 text-center text-[12px] text-ink-500">
            No per-listing data yet. Click Sync analytics to pull views/favorites/sales from Etsy.
          </div>
        ) : (
          byListing.map((l) => {
            const maxV = Math.max(...byListing.map((x) => x.views), 1);
            return (
              <div key={l.id} className="grid grid-cols-[1fr_120px_120px_120px_120px] px-4 py-3 border-b border-cream-200 hover:bg-cream-50/60 transition items-center">
                <div className="min-w-0 pr-3">
                  <div className="text-[12.5px] text-ink-800 truncate tracking-tight font-medium">{l.title}</div>
                  <div className="h-1.5 mt-1.5 rounded-full bg-cream-200 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-etsy-400 to-etsy rounded-full" style={{ width: `${(l.views / maxV) * 100}%` }} />
                  </div>
                </div>
                <div className="text-[12.5px] tabular-nums text-ink-800">{l.views}</div>
                <div className="text-[12.5px] tabular-nums text-ink-800">{l.favorites}</div>
                <div className="text-[12.5px] tabular-nums text-ink-800">{l.sales}</div>
                <div className="text-right text-[12.5px] tabular-nums text-sage font-medium">${l.revenue?.toFixed(2)}</div>
              </div>
            );
          })
        )}
      </Card>
    </div>
  );
}
